import React, { useState, useEffect } from 'react';
import { Package, Clock, DollarSign, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface OrderItem {
  id: number;
  label: string | null;
  item_type: string;
  amount: number;
  quantity: number;
  subtotal_amount: number;
  // Apportionment data for at_closing fees
  apportionments?: {
    id: number;
    party_type: string; // e.g., 'buyer', 'seller', 'association', 'lender'
    party_name?: string;
    percentage: number;
    amount: number;
  }[];
}

interface OrderItemsTabProps {
  items: OrderItem[];
  atClosingItems?: OrderItem[];
  totalAmount: number;
  isDraft?: boolean;
  isSaving?: boolean;
  onSaveItems?: (updatedItems: { id: number; quantity: number; subtotal_amount: number }[]) => void;
}

export const OrderItemsTab: React.FC<OrderItemsTabProps> = ({
  items,
  atClosingItems = [],
  totalAmount,
  isDraft = false,
  isSaving = false,
  onSaveItems,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedItems, setEditedItems] = useState<Map<number, number>>(new Map());

  // Reset edit state when items change (e.g., after save)
  useEffect(() => {
    setEditedItems(new Map());
    setIsEditing(false);
  }, [items]);

  const hasUpfrontItems = items && items.length > 0;
  const hasAtClosingItems = atClosingItems && atClosingItems.length > 0;
  const atClosingTotal = atClosingItems.reduce((sum, item) => sum + item.subtotal_amount, 0);

  const getQuantity = (item: OrderItem) => {
    return editedItems.has(item.id) ? editedItems.get(item.id)! : item.quantity;
  };

  const getSubtotal = (item: OrderItem) => {
    const qty = getQuantity(item);
    return item.amount * qty;
  };

  const getEditedTotal = () => {
    return items.reduce((sum, item) => sum + getSubtotal(item), 0);
  };

  const hasChanges = () => {
    return editedItems.size > 0;
  };

  const handleQuantityChange = (itemId: number, value: string) => {
    const qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 1) return;
    const newEdited = new Map(editedItems);
    const originalItem = items.find(i => i.id === itemId);
    if (originalItem && qty === originalItem.quantity) {
      newEdited.delete(itemId);
    } else {
      newEdited.set(itemId, qty);
    }
    setEditedItems(newEdited);
  };

  const handleSave = () => {
    if (!onSaveItems || !hasChanges()) return;
    const updatedItems = items
      .filter(item => editedItems.has(item.id))
      .map(item => ({
        id: item.id,
        quantity: editedItems.get(item.id)!,
        subtotal_amount: item.amount * editedItems.get(item.id)!,
      }));
    onSaveItems(updatedItems);
  };

  const handleCancel = () => {
    setEditedItems(new Map());
    setIsEditing(false);
  };

  if (!hasUpfrontItems && !hasAtClosingItems) {
    return (
      <div className="text-center py-8">
        <Package className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium">No order items</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This order doesn't have any items yet.
        </p>
      </div>
    );
  }

  const getPartyDisplayName = (partyType: string, partyName?: string) => {
    const typeDisplayNames: { [key: string]: string } = {
      'buyer': 'Buyer',
      'seller': 'Seller',
      'association': 'Association',
      'lender': 'Lender',
      'title_company': 'Title Company',
      'attorney': 'Attorney'
    };

    const displayType = typeDisplayNames[partyType] || partyType.replace(/_/g, ' ');
    return partyName ? `${displayType} (${partyName})` : displayType;
  };

  // Create flattened at closing items with apportionment details
  const atClosingItemsFlattened = atClosingItems.flatMap(item => {
    if (item.apportionments && item.apportionments.length > 0) {
      return item.apportionments.map(apportionment => ({
        id: `${item.id}-${apportionment.id}`,
        label: item.label,
        party_type: apportionment.party_type,
        party_name: apportionment.party_name,
        amount: apportionment.amount,
        percentage: apportionment.percentage,
        total_item_amount: item.subtotal_amount
      }));
    } else {
      // If no apportionment data, assume collected by buyer
      return [{
        id: item.id.toString(),
        label: item.label,
        party_type: 'buyer',
        party_name: undefined,
        amount: item.subtotal_amount,
        percentage: 100,
        total_item_amount: item.subtotal_amount
      }];
    }
  });

  return (
    <div className="space-y-6">
      {/* Upfront Fees Table */}
      {hasUpfrontItems && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <h3 className="font-medium text-lg">Upfront Fees</h3>
            </div>
            {isDraft && onSaveItems && (
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancel}
                      disabled={isSaving}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={!hasChanges() || isSaving}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Quantities
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="rounded-md border">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-3">Item</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-right p-3">Price</th>
                  <th className="text-right p-3">Quantity</th>
                  <th className="text-right p-3">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const qty = getQuantity(item);
                  const subtotal = getSubtotal(item);
                  const isChanged = editedItems.has(item.id);
                  return (
                    <tr key={item.id} className={`border-t ${isChanged ? 'bg-yellow-50' : ''}`}>
                      <td className="p-3">{item.label || `Item #${item.id}`}</td>
                      <td className="p-3">{item.item_type.replace(/_/g, " ")}</td>
                      <td className="p-3 text-right">${item.amount.toFixed(2)}</td>
                      <td className="p-3 text-right">
                        {isEditing ? (
                          <Input
                            type="number"
                            min={1}
                            value={qty}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                            className="w-20 ml-auto text-right"
                          />
                        ) : (
                          qty
                        )}
                      </td>
                      <td className="p-3 text-right">${subtotal.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center p-3 bg-green-50 rounded-md">
            <span className="font-medium text-green-800">Upfront Fees Subtotal</span>
            <span className="font-medium text-green-800">
              ${(isEditing ? getEditedTotal() : totalAmount).toFixed(2)}
              {isEditing && hasChanges() && (
                <span className="text-sm text-orange-600 ml-2">
                  (was ${totalAmount.toFixed(2)})
                </span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* At Closing Fees Table */}
      {hasAtClosingItems && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            <h3 className="font-medium text-lg">At Closing Fees</h3>
          </div>

          <div className="rounded-md border">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-3">Item</th>
                  <th className="text-left p-3">Collected by</th>
                  <th className="text-right p-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {atClosingItemsFlattened.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="p-3">
                      <div>
                        <div>{item.label || `Item #${item.id.split('-')[0]}`}</div>
                        {item.percentage < 100 && (
                          <div className="text-sm text-muted-foreground">
                            ({item.percentage}% of ${item.total_item_amount.toFixed(2)})
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-orange-100 text-orange-800">
                        {getPartyDisplayName(item.party_type, item.party_name)}
                      </span>
                    </td>
                    <td className="p-3 text-right font-medium">${item.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center p-3 bg-orange-50 rounded-md">
            <span className="font-medium text-orange-800">At Closing Fees Subtotal</span>
            <span className="font-medium text-orange-800">${atClosingTotal.toFixed(2)}</span>
          </div>
        </div>
      )}

    </div>
  );
};
